// The ready-list home's data, from hooks the Today and notification screens already use. No new endpoint.
// The practice follows the Today worklist's rule (the login practice if listed, else the primary one, else the
// first) and is locked as the active practice the same way, so an encounter opened from here is scoped to the
// practice whose visits are shown (PROD-1478).
import { useEffect, useMemo, useState } from 'react';
import { useAuthStore } from '@/auth/auth-store';
import { useAppointments } from '@/scheduling/services/use-appointments';
import { useAppointmentMetaData } from '@/scheduling/services/use-appointment-metadata';
import { usePractitionerPractices } from '@/scheduling/services/use-practitioner-practices';
import { useDraftEncounters, draftAppointmentIds } from '@/scheduling/services/use-draft-encounters';
import { bucketAppointments } from '@/scheduling/lib/appointment-buckets';
import { buildPracticeOptions, resolveSelectedPracticeId } from '@/scheduling/lib/practice-options';
import { pickNextAppointment } from '@/scheduling/lib/today-overview';
import { worklistProviderScope } from '@/scheduling/lib/worklist-provider-scope';
import { useAssignedProviders } from '@/scheduling/services/use-assigned-providers';
import { useRole } from '@/auth/use-role';
import { buildReadyRows, needsYouItems } from './ready-list-data';
import { practiceNow } from '@/shared/lib/practice-time';

/** The clock the day strip and the free time read; one tick a minute is enough for a strip of nine hours. */
const TICK_MS = 60_000;

export function useNowMs(): number {
  const [now, setNow] = useState(() => practiceNow().getTime());
  useEffect(() => {
    const id = window.setInterval(() => setNow(practiceNow().getTime()), TICK_MS);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

export function useReadyListData() {
  // Plain values, not `user`, so the memo dependencies below are exactly what they read (React Compiler lint).
  const userId = useAuthStore((s) => s.user?.id);
  const practitionerId = useAuthStore((s) => s.user?.practitionerId);
  const organizationId = useAuthStore((s) => s.user?.organizationId);
  const loginPracticeId = useAuthStore((s) => s.user?.practiceId);
  const loginPracticeName = useAuthStore((s) => s.user?.practiceName);
  const timeZone = useAuthStore((s) => s.user?.timeZone);
  const setActivePractice = useAuthStore((s) => s.setActivePractice);
  const nowMs = useNowMs();

  const { data: practicesData } = usePractitionerPractices(userId);
  const practiceOptions = useMemo(() => {
    const opts = buildPracticeOptions(practicesData);
    if (opts.length) return opts;
    return loginPracticeId ? [{ label: loginPracticeName ?? 'My Practice', value: loginPracticeId }] : [];
  }, [practicesData, loginPracticeName, loginPracticeId]);
  const practiceId = useMemo(
    () => resolveSelectedPracticeId(practiceOptions, practicesData, loginPracticeId ?? ''),
    [practiceOptions, practicesData, loginPracticeId],
  );
  useEffect(() => {
    if (!practiceId || practiceId === loginPracticeId) return;
    const p = practicesData?.find((x) => x.practiceSetupId === practiceId);
    setActivePractice({ practiceSetupId: practiceId, practiceName: p?.practiceName ?? null });
  }, [practiceId, loginPracticeId, practicesData, setActivePractice]);

  // One Today screen for every practitioner (Samer, 4 Nov). Whose visits it lists follows the Today worklist's rule
  // (`worklist-provider-scope.ts`): a physician's own; a nurse's, the providers she supports for this practice today
  // (/Staff/AssignedProviders). No list yet → no request, so a nurse never reads an empty day while it loads.
  const { isNurse } = useRole();
  const assigned = useAssignedProviders(isNurse && practiceId ? { practiceId } : null);
  const providerScope = useMemo(
    () => worklistProviderScope({ isNurse, selfId: practitionerId ?? userId, assignedProviderIds: assigned.data }),
    [isNurse, practitionerId, userId, assigned.data],
  );
  const queryParams = useMemo(() => {
    if (!organizationId || !practiceId || !providerScope) return null;
    return { organizationId, practiceId, ...providerScope, timezone: timeZone, searchText: '' };
  }, [organizationId, providerScope, timeZone, practiceId]);

  const appointments = useAppointments(queryParams);
  const drafts = useDraftEncounters(practitionerId ?? userId);
  const draftApptIds = useMemo(() => draftAppointmentIds(drafts.data), [drafts.data]);
  const buckets = useMemo(
    () => bucketAppointments(appointments.data?.appointments ?? [], draftApptIds),
    [appointments.data, draftApptIds],
  );
  const apptIds = useMemo(() => (appointments.data?.appointments ?? []).map((a) => a.id), [appointments.data]);
  const { data: metaMap } = useAppointmentMetaData(apptIds);
  const rows = useMemo(() => buildReadyRows(buckets, metaMap), [buckets, metaMap]);

  const nameByAppointment = useMemo(() => new Map(rows.map((r) => [r.id, r.card.patientName])), [rows]);
  const needs = useMemo(() => needsYouItems(drafts.data, nameByAppointment, nowMs), [drafts.data, nameByAppointment, nowMs]);
  const nextId = useMemo(
    () => pickNextAppointment(buckets.upcoming, buckets.inProgress, nowMs, buckets.completed),
    [buckets, nowMs],
  );

  return {
    nowMs,
    rows,
    nextId,
    appointments: {
      loading: appointments.isLoading || drafts.isLoading,
      error: appointments.isError,
      retry: () => {
        if (queryParams) void appointments.refetch();
        void drafts.refetch();
      },
    },
    needs: { items: needs, loading: drafts.isLoading, error: drafts.isError, retry: () => void drafts.refetch() },
  };
}
